/* Day Flow — pure bucketing logic for the home "Day flow" widget.
 *
 * Kept free of React/Firebase imports so it can be unit-tested with plain node.
 * The logical day runs 5:00 AM → 4:59 AM (NOT midnight-to-midnight): a task at
 * 1 AM belongs to *tonight's* Late-night section, never to the top of a fresh
 * morning — the classic night-owl failure mode in day-sectioned planners.
 *
 * Buckets are half-open [start, end) in LOGICAL minutes, where real minutes
 * 0:00–4:59 are shifted +1440 so late-night wraps to the end of the day:
 *   Morning     5:00 AM – 11:59 AM   [300, 720)
 *   Afternoon  12:00 PM –  4:59 PM   [720, 1020)
 *   Evening     5:00 PM –  9:59 PM   [1020, 1320)
 *   Late night 10:00 PM –  4:59 AM   [1320, 1740)
 */
export const DAY_BUCKETS = [
  { key: "morning", label: "Morning", icon: "🌅", start: 300, end: 720 },
  { key: "afternoon", label: "Afternoon", icon: "☀️", start: 720, end: 1020 },
  { key: "evening", label: "Evening", icon: "🌆", start: 1020, end: 1320 },
  { key: "latenight", label: "Late night", icon: "🌙", start: 1320, end: 1740 },
];

// Real minutes-of-day (0–1439) → logical minutes (300–1739). 0:00–4:59 wraps to day end.
export function toLogicalMins(mins) {
  return mins < 300 ? mins + 1440 : mins;
}

// mins → bucket key, or null for null/invalid input. Single source of truth for
// both the item bucketer and the "now" highlighter, so a boundary minute can
// never land in one section while the now-pill claims another.
export function dayFlowBucketKey(mins) {
  if (mins == null || isNaN(mins) || mins < 0 || mins > 1439) return null;
  const lm = toLogicalMins(mins);
  const b = DAY_BUCKETS.find(x => lm >= x.start && lm < x.end);
  return b ? b.key : null;
}

// 540 → "9:00 AM". Display-only.
export function fmtMins(mins) {
  if (mins == null || isNaN(mins)) return "";
  let h = Math.floor(mins / 60), mn = mins % 60;
  const ap = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12; else if (h > 12) h -= 12;
  return `${h}:${String(mn).padStart(2, "0")} ${ap}`;
}

/* items: [{ id, kind, name, icon, mins, done }] — pre-merged, pre-deduped, timed only.
 * nowMins: current real minutes-of-day, or null to skip now-detection.
 * Returns { sections, nowKey }:
 *   sections — non-empty buckets in logical-day order, each
 *     { key, label, icon, items (undone by time asc, then done by time asc),
 *       doneCount, total, allDone }
 *   nowKey — bucket key the clock is currently inside (independent of whether
 *     that section has items; the widget decides how to surface it).
 */
export function buildDayFlowSections(items, nowMins) {
  const byKey = {};
  for (const it of items) {
    const k = dayFlowBucketKey(it.mins);
    if (!k) continue; // untimed/invalid items never get a fabricated slot
    (byKey[k] = byKey[k] || []).push(it);
  }
  const sections = [];
  for (const b of DAY_BUCKETS) {
    const list = byKey[b.key];
    if (!list || !list.length) continue; // empty sections don't render
    list.sort((a, z) =>
      a.done !== z.done ? (a.done ? 1 : -1) : toLogicalMins(a.mins) - toLogicalMins(z.mins)
    );
    const doneCount = list.filter(x => x.done).length;
    sections.push({
      key: b.key, label: b.label, icon: b.icon,
      items: list, doneCount, total: list.length,
      allDone: doneCount === list.length,
    });
  }
  return { sections, nowKey: dayFlowBucketKey(nowMins) };
}
